import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Plus } from "lucide-react";
import { Logo, KidAvatar } from "../components/Brand";
import { PinPad } from "../components/PinPad";
import { ParentGate } from "../components/ParentGate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { parentApi, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Profiles() {
  const [kids, setKids] = useState(null);
  const [picked, setPicked] = useState(null);
  const [pinErr, setPinErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(false);
  const { loginKid, logout, lockParent } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    lockParent();
    parentApi.get("/kids").then((r) => setKids(r.data));
  }, [lockParent]);

  const tryPin = async (pin) => {
    setBusy(true);
    setPinErr("");
    try {
      const { data } = await parentApi.post(`/kids/${picked.id}/login`, { pin });
      loginKid(data);
      nav("/kid");
    } catch (e) {
      setPinErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF7EC] pt-[env(safe-area-inset-top)]">
      <header className="max-w-6xl mx-auto px-4 sm:px-8 h-24 flex items-center justify-between">
        <Logo />
        <div className="flex gap-3">
          <button data-testid="profiles-parent-zone-btn" onClick={() => setGate(true)} className="zn-btn bg-white !py-2"><Lock size={16} /> Parent zone</button>
          <button data-testid="profiles-logout-btn" onClick={() => { logout(); nav("/"); }} className="zn-btn bg-white !py-2 !px-3" title="Log out"><LogOut size={16} /></button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-12 text-center">
        <h1 className="text-5xl sm:text-6xl font-bold mb-3 zn-pop">Who's creating today?</h1>
        <p className="text-lg text-[#1B1A2E]/70 mb-14">Tap your picture and enter your secret PIN.</p>
        {kids && kids.length === 0 && (
          <div data-testid="no-kids-state" className="zn-card max-w-md mx-auto p-8">
            <p className="mb-5 font-semibold">No kid profiles yet. Ask a grown-up to add one!</p>
            <button data-testid="no-kids-add-btn" onClick={() => setGate(true)} className="zn-btn bg-[#FFD23F]"><Plus size={18} /> Add a profile</button>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-10">
          {kids?.map((k, i) => (
            <button key={k.id} data-testid={`profile-card-${k.name.toLowerCase()}`} onClick={() => { setPinErr(""); setPicked(k); }}
              className="group zn-pop flex flex-col items-center gap-4" style={{ animationDelay: `${i * 80}ms` }}>
              <KidAvatar avatar={k.avatar} color={k.color} frame={k.frame} size={150} className="shadow-[6px_6px_0_0_#1B1A2E] group-hover:-translate-y-2 group-hover:rotate-3 transition-transform" />
              <span className="font-display text-2xl font-semibold">{k.name}</span>
            </button>
          ))}
        </div>
      </main>
      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent data-testid="pin-dialog" className="border-[3px] border-[#1B1A2E] rounded-3xl shadow-[6px_6px_0_0_#1B1A2E] max-w-sm">
          <DialogHeader className="items-center text-center">
            {picked && <KidAvatar avatar={picked.avatar} color={picked.color} frame={picked.frame} size={72} />}
            <DialogTitle className="font-display text-3xl">Hi {picked?.name}!</DialogTitle>
            <DialogDescription>Type your secret 4-digit PIN</DialogDescription>
          </DialogHeader>
          {picked && <PinPad onComplete={tryPin} error={pinErr} busy={busy} />}
        </DialogContent>
      </Dialog>
      <ParentGate open={gate} onOpenChange={setGate} />
    </div>
  );
}
